import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import { Router } from "@angular/router";
import { AuthService } from "../auth.service";
import { Involved } from "../model/involved";

@Injectable({
  providedIn: "root",
})
export class InvolvedService {
  private apiUrl: string;
  private byIdUrl: string;
  private _currentInvolved: BehaviorSubject<Involved[]>;
  public selectedInvolved: BehaviorSubject<Involved | null>;
  private _formState = new BehaviorSubject<Partial<Involved>>({});
  constructor(
    private http: HttpClient,
    private env: EnvironmentHelper,
    private router: Router,
    private auth: AuthService
  ) {
    this._currentInvolved = new BehaviorSubject<Involved[]>([]);
    this.selectedInvolved = new BehaviorSubject<Involved | null>(null);
    this.apiUrl = `${env.apiBaseUrl}involved/`;
    this.byIdUrl = `${env.apiBaseUrl}involved/`;
  }

  public createInvolved(
    token: string,
    ticketId: number,
    involved: Involved
  ): Observable<Messages> {
    const url = `${this.apiUrl}`;
    return new Observable<Messages>((observer) => {
      this.http
        .post<any>(url, involved, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveInvolved(response, observer),
          (error) => this.receiveInvolved(error, observer)
        );
    });
  }

  public deleteInvolved(
    token: string,
    ticketId: number,
    involvedId: number
  ): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${involvedId}`;

    return new Observable<Messages>((observer) => {
      this.http
        .delete<any>(url, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveInvolved(response, observer),
          (error) => this.receiveInvolved(error, observer)
        );
    });
  }

  private receiveInvolveds(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Involved[]>)?.body;
      if (!data) {
        observer.next(Messages.from({ involved: ["involved.unknown"] }));
        return;
      }
      this._currentInvolved.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "auth",
          "unauthorized.access."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "involved",
          "involved.incomplete"
        )
      );
    }
  }

  private receiveInvolved(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Involved>)?.body;
      if (!data) {
        observer.next(Messages.from({ involved: ["involved.unknown"] }));
        return;
      }
      this.selectedInvolved.next(data);
      observer.next(Messages.EMPTY);
    } else if (
      response.status === 400 ||
      response.status === 401 ||
      response.status === 409 ||
      response.status === 500
    ) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "involved",
          "involved.load.error"
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "general",
          "an.unexpected.error.has.occurred."
        )
      );
    }
  }

  public getInvolvedById(
    token: string,
    involvedId: number
  ): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(`${this.byIdUrl}${involvedId}`, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receiveInvolveds(response, observer),
          (error) => this.receiveInvolveds(error, observer)
        );
    });
  }

  public selectFromRouter() {
    const segments = this.getLastSuccessfulNavigationSegments();

    if (segments && segments.length > 0) {
      const taskId = segments[segments.length - 1];
      const token = this.auth.token;

      this.getInvolvedById(token, Number(taskId)).subscribe();
    } else {
      const emptyTask = new Involved(0, 0, "");
      this.select(emptyTask);
    }
  }

  private getLastSuccessfulNavigationSegments(): string[] | null {
    const segments =
      this.router.lastSuccessfulNavigation?.finalUrl?.root.children["primary"]
        .segments;
    return segments ? segments.map((segment) => segment.path) : null;
  }

  public select(involved: Involved) {
    this.selectedInvolved.next(involved);
  }
  public accept(involved: Involved[]) {
    this._currentInvolved.next(involved);
  }

  public get selectedInvolvedObservable(): Observable<Involved | null> {
    return this.selectedInvolved.asObservable();
  }

  public get involvedObservable(): Observable<Involved[]> {
    return this._currentInvolved.asObservable();
  }

  public get InvolvedData(): Involved[] {
    return this._currentInvolved.value;
  }

  public get currentInvolved(): Involved {
    const value = this.selectedInvolved.value;
    return value || Involved.EMPTY;
  }
  get formState(): Observable<Partial<Involved>> {
    return this._formState.asObservable();
  }
  setFormState(state: Partial<Involved>) {
    localStorage.setItem("involvedFormBackup", JSON.stringify(state));
    this._formState.next(state);
  }

  restoreFormState(emit: boolean = true): Partial<Involved> {
    const savedState = localStorage.getItem("involvedFormBackup");
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        if (emit) {
          this._formState.next(parsedState);
        }
        return parsedState;
      } catch (e) {}
    }

    return {};
  }

  clearFormStateBackup() {
    localStorage.removeItem("involvedFormBackup");
  }
}
